import { Link } from "react-router-dom";
import {
  Plus,
  CheckCircle,
  XCircle,
  LogIn,
  Edit,
  DollarSign,
  AlertTriangle,
  Activity,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

export type AuditLogItem = {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  actor_name: string;
  created_at: string;
  details?: Record<string, unknown> | null;
};

type ActivityFeedProps = {
  items: AuditLogItem[];
  loading?: boolean;
  maxItems?: number;
};

type IconConfig = {
  icon: React.ElementType;
  bg: string;
  color: string;
};

function getIconConfig(action: string): IconConfig {
  const normalized = action.toLowerCase();

  if (normalized.includes("creat") || normalized.includes("add") || normalized.includes("register")) {
    return { icon: Plus, bg: "bg-green-100", color: "text-green-600" };
  }
  if (normalized.includes("approv")) {
    return { icon: CheckCircle, bg: "bg-green-100", color: "text-green-600" };
  }
  if (normalized.includes("deni") || normalized.includes("denied") || normalized.includes("delet") || normalized.includes("remov")) {
    return { icon: XCircle, bg: "bg-red-100", color: "text-red-600" };
  }
  if (normalized.includes("login") || normalized.includes("sign_in") || normalized.includes("signed_in")) {
    return { icon: LogIn, bg: "bg-blue-100", color: "text-blue-600" };
  }
  if (normalized.includes("updat") || normalized.includes("edit") || normalized.includes("modif") || normalized.includes("chang")) {
    return { icon: Edit, bg: "bg-amber-100", color: "text-amber-600" };
  }
  if (normalized.includes("payment") || normalized.includes("paid") || normalized.includes("invoice") || normalized.includes("charge")) {
    return { icon: DollarSign, bg: "bg-purple-100", color: "text-purple-600" };
  }
  if (normalized.includes("violation") || normalized.includes("violat") || normalized.includes("breach") || normalized.includes("over_limit")) {
    return { icon: AlertTriangle, bg: "bg-red-100", color: "text-red-600" };
  }

  return { icon: Activity, bg: "bg-gray-100", color: "text-gray-500" };
}

function formatAction(action: string): string {
  return action.replace(/_/g, " ").toLowerCase();
}

function formatEntityType(entityType: string): string {
  return entityType.replace(/_/g, " ").toLowerCase();
}

function RelativeTime({ dateString }: { dateString: string }) {
  try {
    return (
      <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">
        {formatDistanceToNow(new Date(dateString), { addSuffix: true })}
      </span>
    );
  } catch {
    return <span className="text-xs text-gray-400">—</span>;
  }
}

function SkeletonItem() {
  return (
    <div className="flex items-start gap-3 py-3">
      <Skeleton className="h-8 w-8 rounded-full shrink-0" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-3.5 w-3/4" />
        <Skeleton className="h-3 w-1/4" />
      </div>
    </div>
  );
}

export function ActivityFeed({ items, loading = false, maxItems = 20 }: ActivityFeedProps) {
  const displayItems = items.slice(0, maxItems);

  return (
    <div className="divide-y divide-gray-100">
      {loading ? (
        Array.from({ length: 5 }).map((_, i) => <SkeletonItem key={i} />)
      ) : displayItems.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-400">
          No recent activity
        </div>
      ) : (
        displayItems.map((item) => {
          const { icon: Icon, bg, color } = getIconConfig(item.action);
          return (
            <div key={item.id} className="flex items-start gap-3 py-3">
              <div
                className={cn(
                  "h-8 w-8 rounded-full flex items-center justify-center shrink-0 mt-0.5",
                  bg,
                )}
              >
                <Icon className={cn("h-4 w-4", color)} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700 leading-snug line-clamp-2">
                  <span className="font-semibold text-gray-900">{item.actor_name}</span>{" "}
                  {formatAction(item.action)}{" "}
                  {formatEntityType(item.entity_type)}
                  {item.entity_id && (
                    <span className="text-gray-400"> #{item.entity_id.slice(0, 8)}</span>
                  )}
                </p>
              </div>
              <RelativeTime dateString={item.created_at} />
            </div>
          );
        })
      )}

      {!loading && items.length > 0 && (
        <div className="pt-3">
          <Link
            to="/admin/audit-logs"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors whitespace-nowrap"
          >
            View all logs
          </Link>
        </div>
      )}
    </div>
  );
}
